<template>
  <div data-focus-view class="home-container">
    <div class="tw:!flex tw:!justify-center tw:!mb-6">
      <div class="tw:!relative tw:!w-full tw:!max-w-lg">
        <Search class="search-icon tw:!absolute tw:!left-4 tw:!top-1/2 tw:!-translate-y-1/2 tw:!w-5 tw:!h-5 tw:!pointer-events-none" />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="showAll ? '' : $t('search.placeholder')"
          @focus="showAll = true"
          @blur="showAll = false"
          class="search-input tw:!w-full tw:!pl-11 tw:!pr-10 tw:!py-3.5 tw:!rounded-full tw:!text-sm tw:!font-semibold tw:!outline-none tw:!transition-all tw:!box-border"
        />
        <button
          v-if="searchQuery"
          @click="searchQuery = ''"
          class="tw:!absolute tw:!right-4 tw:!top-1/2 tw:!-translate-y-1/2 tw:!text-gray-400 hover:tw:!text-gray-600 tw:!bg-transparent tw:!border-0 tw:!cursor-pointer tw:!p-0"
        >
          <X class="tw:!w-4 tw:!h-4" />
        </button>
      </div>
    </div>

    <div v-if="!searchQuery" class="tw:!flex tw:!justify-center" style="margin-bottom: 2.5rem;">
      <div class="tabs-container">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="tab-btn"
          :class="{ 'active': activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div class="tw!:mx-auto tw!:max-w-6xl">
      <div v-if="searchQuery" class="tw:!mb-4 tw:!text-sm tw:!text-gray-500">
        <span v-if="visibleList.length > 0">
          {{ visibleList.length }} {{ visibleList.length === 1 ? $t('search.results') : $t('search.resultsPlural') }}
        </span>
        <span v-else class="tw:!flex tw:!items-center tw:!gap-2">
          {{ $t('search.noResults') }}
        </span>
      </div>

      <div class="grid-scroll-wrapper" :class="{ 'scroll-all': showAll || !!searchQuery }">
        <div class="grid-layout">
          <div
            v-for="item in visibleList"
            :key="item.slug"
            @click="$emit('select-country', item.slug, item.name)"
          >
            <div class="country-card">
              <div class="tw:flex tw:items-center tw:justify-between">
                <div class="tw:flex tw:items-center tw:gap-5">
                  <div style="flex-shrink: 0;">
                    <img
                      :src="item.flag"
                      :alt="item.name"
                      class="tw:!block tw:!w-12 tw:!h-auto tw:!rounded-lg tw:!border tw:!border-gray-200 tw:!object-contain"
                    />
                  </div>
                  <div class="tw:flex tw:flex-col">
                    <span style="color: #111827; font-size: 1rem; font-weight: 700;">
                      {{ item.name }}
                    </span>
                    <span class="tw:!text-xs tw:!font-semibold od-price">FROM $3.85</span>
                  </div>
                </div>
                <ArrowRight class="tw:w-5 tw:h-5 tw:text-gray-300 group-hover:tw:text-yellow-500 tw:transition-colors tw:flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!searchQuery && !showAll && displayCount > 9" class="tw:flex tw:justify-center" style="margin-top: 2.5rem;">
        <button class="view-all-btn" @click="showAll = true">
          {{ $t('button.viewAll') }}
          <ChevronDown class="tw:w-5 tw:h-5 tw:ml-2" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CountriesData from '~/assets/countriesData.json'
import { getFlag } from '~/utility/helper'
import { Search, X, ArrowRight, ChevronDown } from 'lucide-vue-next'

type TabValue = 'countries' | 'regions'

defineEmits<{
  'select-country': [slug: string, name: string]
}>()

const { t } = useI18n()

const tabs = computed(() => [
  { value: 'countries' as TabValue, label: t('tab.countries') },
  { value: 'regions' as TabValue, label: t('tab.regions') },
])

const activeTab = ref<TabValue>('countries')
const showAll = ref(false)
const searchQuery = ref('')

const PRIORITY_CODES = ['MC', 'IN', 'JP', 'ID', 'BD', 'PK', 'NG', 'GH', 'US', 'CN']

const allItems = computed(() => {
  return CountriesData.map(item => ({
    name: item.name,
    slug: item.code,
    flag: getFlag(item.code),
    type: item.type,
  })).sort((a, b) => 
    +PRIORITY_CODES.includes(b.slug) - +PRIORITY_CODES.includes(a.slug)
  )
})

const visibleList = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()

  if (q) return allItems.value.filter(item => item.name.toLowerCase().includes(q))

  const typeFilter = activeTab.value === 'countries' ? 1 : 2
  const filtered = allItems.value.filter(item => item.type === typeFilter)

  return showAll.value ? filtered : filtered.slice(0, 9)
})

const displayCount = computed(() =>
  allItems.value.filter(item => item.type === (activeTab.value === 'countries' ? 1 : 2)).length
)
</script>

<style scoped>
.home-container {
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.grid-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  width: 100%;
}
@media (min-width: 1024px) { .grid-layout { grid-template-columns: repeat(3, 1fr); } }

@media (max-width: 767px) {
  .grid-scroll-wrapper {
    height: 86vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-right: 0.25rem;
    scrollbar-width: thin;
    scrollbar-color: #e5e7eb transparent;
  }

  .grid-scroll-wrapper::-webkit-scrollbar {
    width: 4px;
  }

  .grid-scroll-wrapper::-webkit-scrollbar-track {
    background: transparent;
  }

  .grid-scroll-wrapper::-webkit-scrollbar-thumb {
    background-color: #e5e7eb;
    border-radius: 9999px;
  }
}

.grid-scroll-wrapper.scroll-all {
  max-height: 70vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}

.grid-scroll-wrapper.scroll-all::-webkit-scrollbar {
  width: 6px;
}

.grid-scroll-wrapper.scroll-all::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 9999px;
}

.tabs-container {
  display: inline-flex;
  background-color: white;
  padding: 0.375rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
}

.tab-btn {
  border-radius: 9999px;
  padding: 0.625rem 2rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  background: transparent;
  color: #6b7280;
  border: none;
  transition: all 0.2s ease-in-out;
}

.tab-btn:hover { color: #111827; }

.tab-btn.active {
  background-color: #f3f4f6;
  color: #111827;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.country-card {
  display: block;
  background-color: #f4f2f2;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 0.75rem 1rem;
  box-sizing: border-box;
  transition: all 0.3s ease;
  height: 100%;
}
.country-card:hover {
  border-color: var(--esim-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.flag-container {
  position: relative;
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
}

.flag-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform: none;
}

.view-all-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.875rem 2.5rem;
  background-color: var(--esim-primary);
  color: var(--esim-dark-btn-text);
  font-size: 0.875rem;
  font-weight: 700;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.view-all-btn:hover {
  background-color: var(--esim-primary-active);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}

.search-input {
  background-color: var(--esim-background);
  color: #111827;
  border: 2px solid var(--esim-primary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.search-input::placeholder {
  color: #111827;
  font-weight: 500;
}

.search-input:focus {
  border-color: var(--esim-primary-active, var(--esim-primary));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--esim-primary) 15%, transparent);
}

.search-icon {
  color: var(--esim-primary);
}
</style>